import {
  addDoc,
  collection,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import db, { BASE_FUNCTIONS_URL } from "../types/firebase";
import { ApplicantDoc } from "../types/applicants";
import {
  TermsAcceptance,
  TermsDocumentType,
  TermsLanguage,
} from "../types/terms-acceptance";

export type ExistingTermsAcceptances = Partial<
  Record<TermsDocumentType, TermsAcceptance>
>;

type TermsResolveResponse = {
  data: {
    status: string;
    message?: string;
    applicantDoc?: ApplicantDoc;
    existingAcceptances?: ExistingTermsAcceptances;
  };
};

type TermsAcceptResponse = {
  data: {
    status: string;
    message?: string;
    id?: string;
    acceptance?: TermsAcceptance;
  };
};

const postPublicTermsApi = async <T>(
  path: string,
  payload: Record<string, unknown>,
): Promise<T> => {
  const apiKey = process.env.REACT_APP_FUNCTIONS_API_KEY || "";
  const response = await fetch(`${BASE_FUNCTIONS_URL}/public${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": apiKey,
    },
    body: JSON.stringify(payload),
  });

  const responseText = await response.text();
  let responseData: T | null = null;

  if (responseText) {
    try {
      responseData = JSON.parse(responseText) as T;
    } catch (error) {
      if (!response.ok) {
        throw new Error(responseText);
      }

      throw new Error("Server returned an invalid JSON response.");
    }
  }

  if (!response.ok) {
    const errorMessage =
      typeof (responseData as { data?: { message?: string } } | null)?.data
        ?.message === "string"
        ? (responseData as { data?: { message?: string } }).data?.message
        : responseText || "Request failed.";
    throw new Error(errorMessage);
  }

  if (!responseData) {
    throw new Error("Server returned an empty response.");
  }

  return responseData;
};

export const findApplicantByEmail = async (
  email: string,
): Promise<ApplicantDoc | null> => {
  const normalizedEmail = email.trim().toLocaleLowerCase();

  if (!normalizedEmail) {
    return null;
  }

  const applicantQuery = query(
    collection(db, "applicants"),
    where("email", "==", normalizedEmail),
    limit(1),
  );
  const applicantSnapshot = await getDocs(applicantQuery);

  if (applicantSnapshot.empty) {
    return null;
  }

  const applicantDoc = applicantSnapshot.docs[0];
  return {
    id: applicantDoc.id,
    applicant: applicantDoc.data(),
  } as ApplicantDoc;
};

export const saveTermsAcceptance = async (acceptance: TermsAcceptance) => {
  const docRef = await addDoc(collection(db, "termsAcceptances"), acceptance);
  return docRef.id;
};

export const resolveTermsApplicant = async (
  email: string,
): Promise<{
  applicantDoc: ApplicantDoc;
  existingAcceptances: ExistingTermsAcceptances;
} | null> => {
  const response = await postPublicTermsApi<TermsResolveResponse>(
    "/terms-acceptance/resolve",
    {
      email,
    },
  );

  if (!(response.data.applicantDoc && response.data.existingAcceptances)) {
    return null;
  }

  return {
    applicantDoc: response.data.applicantDoc,
    existingAcceptances: response.data.existingAcceptances,
  };
};

export const submitTermsAcceptance = async (payload: {
  applicantId: string;
  email: string;
  documentType: TermsDocumentType;
  language: TermsLanguage;
  optionalNewServicesAccepted?: boolean;
}) => {
  const response = await postPublicTermsApi<TermsAcceptResponse>(
    "/terms-acceptance/accept",
    payload,
  );

  if (!response.data.acceptance) {
    throw new Error(response.data.message || "Acceptance could not be saved.");
  }

  return response.data.acceptance;
};

export const getExistingAcceptances = async (
  applicantId: string,
): Promise<ExistingTermsAcceptances> => {
  if (!applicantId) {
    return {};
  }

  const acceptancesQuery = query(
    collection(db, "termsAcceptances"),
    where("applicantId", "==", applicantId),
  );
  const acceptancesSnapshot = await getDocs(acceptancesQuery);

  const latestAcceptances: ExistingTermsAcceptances = {};

  acceptancesSnapshot.forEach((docSnapshot) => {
    const acceptance = docSnapshot.data() as TermsAcceptance;
    const currentAcceptance = latestAcceptances[acceptance.documentType];

    if (
      !currentAcceptance ||
      currentAcceptance.acceptedAt < acceptance.acceptedAt
    ) {
      latestAcceptances[acceptance.documentType] = acceptance;
    }
  });

  return latestAcceptances;
};
